/**
 * eGIS - 교육용 GIS 웹 애플리케이션
 * 메인 엔트리 포인트
 */

import './styles/main.css';
import { AppLayout } from './ui/layout/AppLayout.js';
import { initMobileShell } from './ui/layout/MobileShell.js';
import { initToolbarGroups } from './ui/layout/toolbarGroups.js';
import { initCopyrightToggle } from './ui/layout/copyrightToggle.js';
import { initFullscreenToggle } from './ui/layout/fullscreenToggle.js';
import { initInstallPrompt } from './ui/layout/installPrompt.js';
import { mapManager } from './core/MapManager.js';
import { layerManager } from './core/LayerManager.js';
import { themeManager } from './utils/ThemeManager.js';
import { askText } from './utils/askText.js';
import { escapeHtml } from './utils/escapeHtml.js';
import { eventBus, Events } from './utils/EventBus.js';
import { LayerPanel } from './ui/panels/LayerPanel.js';
import { BrowserPanel } from './ui/panels/BrowserPanel.js';
import { toolManager } from './tools/ToolManager.js';
import { attributeTable } from './ui/panels/AttributeTable.js';
import { coordinateSystem } from './core/CoordinateSystem.js';
import { projectManager } from './core/ProjectManager.js';
// 진단용 훅에 노출 — 헤드리스 검증에서 마커·도로망 상태를 확인한다
import { isochroneTool } from './tools/IsochroneTool.js';
import { roadNetwork } from './core/RoadNetwork.js';
import { measureTool } from './tools/MeasureTool.js';
import { autoSaveManager } from './core/AutoSaveManager.js';
import { historyManager } from './core/HistoryManager.js';
import { selectTool } from './tools/SelectTool.js';
import { featureInfoCard } from './ui/FeatureInfoCard.js';
import { featureEditTool } from './tools/FeatureEditTool.js';
import { choroplethPanel } from './ui/panels/ChoroplethPanel.js';
import { tableJoinPanel } from './ui/panels/TableJoinPanel.js';
import { layerMergePanel } from './ui/panels/LayerMergePanel.js';
import { layerSplitPanel } from './ui/panels/LayerSplitPanel.js';
import { labelPanel } from './ui/panels/LabelPanel.js';
import { bufferPanel } from './ui/panels/BufferPanel.js';
import { gridPanel } from './ui/panels/GridPanel.js';
import { voronoiPanel } from './ui/panels/VoronoiPanel.js';
import { exportPanel } from './ui/panels/ExportPanel.js';
import { fieldCalculatorPanel } from './ui/panels/FieldCalculatorPanel.js';
import { spatialOperationsPanel } from './ui/panels/SpatialOperationsPanel.js';
import { heatmapPanel } from './ui/panels/HeatmapPanel.js';
import { flowPanel } from './ui/panels/FlowPanel.js';
import { bookmarkPanel } from './ui/panels/BookmarkPanel.js';
import { coordinateImportPanel } from './ui/panels/CoordinateImportPanel.js';
import { georeferencePanel } from './ui/panels/GeoreferencePanel.js';
import { chartMapPanel } from './ui/panels/ChartMapPanel.js';
import { isochronePanel } from './ui/panels/IsochronePanel.js';
import { routingPanel } from './ui/panels/RoutingPanel.js';
import { geocodingPanel } from './ui/panels/GeocodingPanel.js';
import { labPanel } from './ui/panels/LabPanel.js';
import { labs } from './labs/labs.js';
import { EXPERIMENT_IDS } from './labs/registry.js';
import { bindGlass } from './labs/glass.js';
import { bindLabsButton } from './labs/labsButton.js';
import { bindClassFill } from './labs/classFillBinding.js';
import { classFillPopover } from './ui/panels/ClassFillPopover.js';
import { choroplethTool } from './tools/ChoroplethTool.js';
import { builtinDataManager } from './core/BuiltinDataManager.js';
import { drawingPanel } from './ui/panels/DrawingPanel.js';
import { layerExportPanel } from './ui/panels/LayerExportPanel.js';
import { cartogramPanel } from './ui/panels/CartogramPanel.js';
import { rasterAnalysisPanel } from './ui/panels/RasterAnalysisPanel.js';
import { cloudPanel } from './ui/panels/CloudPanel.js';
import { myPagePanel } from './ui/panels/MyPagePanel.js';
import { supabaseManager } from './core/SupabaseManager.js';
import { geocodingService } from './services/GeocodingService.js';
import { fromLonLat } from 'ol/proj';
import { transformExtent } from 'ol/proj';
import GeoJSON from 'ol/format/GeoJSON';
import { geojsonLoader } from './loaders/GeoJSONLoader.js';
import { shapefileLoader } from './loaders/ShapefileLoader.js';
import { geopackageLoader } from './loaders/GeoPackageLoader.js';
import { demLoader } from './loaders/DEMLoader.js';
import { isShapefileComponent } from './utils/fileCollector.js';
import { visitorTracker } from './core/VisitorTracker.js';
import { builtinDataDialog } from './ui/dialogs/BuiltinDataDialog.js';
import { setCrsPrompt } from './core/crsResolver.js';
import { crsConfirmDialog } from './ui/dialogs/CrsConfirmDialog.js';
import { View3DPanel } from './ui/panels/View3DPanel.js';
import { SwipePanel } from './ui/panels/SwipePanel.js';
import { GlobePanel } from './ui/panels/GlobePanel.js';
import { timeSeriesPanel } from './ui/panels/TimeSeriesPanel.js';
import { timeSeriesTool } from './tools/TimeSeriesTool.js';
import { AnimationExportDialog } from './ui/panels/AnimationExportDialog.js';
import { captureFrames, encodeGif, recordVideo } from './tools/animationExportCanvas.js';
import { saveBlobAs } from './utils/saveFile.js';
import { bindLabMenuItems } from './labs/labMenu.js';

// 레이아웃·휴대폰 셸 — initApp 에서 만들고, 메뉴 동작(view-toggle-panel)이 같은 경로로 시트를 토글한다
let layout = null;
let mobileShell = null;

/**
 * 앱 초기화
 */
function initApp() {
  console.log('eGIS 시작!');

  // 0. 히스토리 관리자 초기화
  historyManager.init();

  // 1. 좌표계 시스템 초기화
  coordinateSystem.init();

  // 좌표계가 애매할 때 물어볼 창을 등록한다.
  // core/는 ui/를 모르므로 여기서 이어 붙인다.
  setCrsPrompt((detection, context) => crsConfirmDialog.pick(detection, context));

  // 2. 테마 초기화
  themeManager.init();

  // 3. 레이아웃 렌더링
  layout = new AppLayout('app');
  layout.render();

  // 3.2 휴대폰 셸 — 툴바·메뉴를 서랍으로, 검색을 검색 줄로 옮기고 레이어 버튼·배지를 돌린다
  mobileShell = initMobileShell({ layout, layerManager, eventBus, Events });

  // 3.5 실험실 — 저장값과 ?lab= 을 읽고, 켜자마자 반영되는 것(글래스·배지)을 묶는다
  labs.init({
    search: window.location.search,
    storage: window.localStorage,
    knownIds: EXPERIMENT_IDS,
    baseUrl: window.location.origin + window.location.pathname
  });
  bindGlass(labs);
  bindLabsButton(labs, document.getElementById('labs-toggle'));
  bindClassFill(labs, { mapEl: document.getElementById('map'), tool: choroplethTool, popover: classFillPopover });

  // 실험 메뉴 항목(data-lab) 숨김/표시 — 켜고 끄면 바로 반영
  bindLabMenuItems(labs);

  // 시계열: 실험을 켜면 이미 있는 시계열 레이어의 컨트롤을 되살리고, 끄면 컨트롤만 걷는다
  // (레이어는 저장된 연도의 정적 단계구분도로 남는다)
  // 설정 창 안내는 기본 alert 그대로 — 같은 모달 규약의 ChoroplethPanel 도 alert 이고,
  // 모달이 열린 동안 상태표시줄 문구는 눈에 띄지 않는다
  labs.onChange((id, on) => {
    if (id !== 'time-series') return;
    if (on) timeSeriesTool.restoreControls();
    else {
      animationDialog?.cancel();   // 만들던 애니메이션은 버린다
      timeSeriesTool.detach();
    }
  });
  // 복원 뒤(프로젝트 열기·자동 복원 완료) 컨트롤 되살리기.
  // 이 리스너는 restoreState/loadProject 의 try 안에서 불리므로 여기서 던지면 복원 전체가
  // 실패로 찍힌다 — 스스로 잡는다. (autoSaveManager.init 보다 앞이라 STATE_RESTORED 를 놓치지 않는다)
  const restoreTimeSeries = () => {
    if (!labs.isOn('time-series')) return;
    try {
      timeSeriesTool.restoreControls();
    } catch (e) {
      console.error('시계열 컨트롤 복원 실패:', e);
    }
  };
  eventBus.on(Events.PROJECT_LOADED, restoreTimeSeries);
  eventBus.on(Events.STATE_RESTORED, restoreTimeSeries);
  eventBus.on(Events.PROJECT_NEW, () => {
    animationDialog?.cancel();   // 레이어가 곧 사라진다 — 캡처 중이면 중단
    timeSeriesTool.detach();
  });

  // 슬라이더의 「저장」 → 애니메이션 저장 대화상자. 찍기 전에 재생을 멈추고, 3D·지구본·
  // 스와이프가 켜져 있으면 먼저 끈다(지도 캔버스가 평면 2D 그대로여야 프레임이 맞다).
  // view3dPanel·globePanel·swipePanel 은 아래에서 대입된다 — 버튼을 누를 때는 이미 있다.
  timeSeriesTool.onSave = (layerId) => {
    const cfg = timeSeriesTool.config(layerId);
    const info = layerManager.getLayer(layerId);
    if (!cfg || !info) return;
    animationDialog?.cancel();
    const dialog = new AnimationExportDialog({
      layerName: info.name,
      fields: cfg.timeSeries.fields,
      speed: timeSeriesTool.speed,
      hasMediaRecorder: typeof window.MediaRecorder === 'function',
      isTypeSupported: (m) => window.MediaRecorder.isTypeSupported(m),
      beforeCapture: async () => {
        timeSeriesTool.pause();
        swipePanel?.close();
        globePanel?.exitIfActive();
        if (view3dPanel?.controller) await view3dPanel.toggle();
      },
      captureFrames: (opts) => captureFrames({ tool: timeSeriesTool, layerId, ...opts }),
      encodeGif,
      recordVideo,
      saveBlobAs,
      onMessage: showStatusMessage
    });
    animationDialog = dialog;
    dialog.show();
  };

  // 4. 지도 초기화
  mapManager.init('map', {
    center: [127.5, 36.5],
    zoom: 7
  });

  // 4.5 자동 저장 관리자 초기화 (지도 초기화 후)
  autoSaveManager.init();

  // 4.6 Supabase 클라우드 관리자 초기화
  supabaseManager.init().then(() => {
    updateHeaderAuth();
    visitorTracker.init();
  });

  // 4.7 헤더 로그인 버튼 이벤트
  initHeaderAuth();

  // 5. 패널 초기화
  new LayerPanel('layer-list');
  new BrowserPanel('file-drop-zone');

  // 3D 보기와 지구본은 배타 — 서로 켜기 전에 상대를 끈다
  view3dPanel = new View3DPanel({
    mapManager,
    layerManager,
    onMessage: showStatusMessage,
    beforeEnter: () => globePanel?.exitIfActive()
  });
  view3dPanel.init();

  globePanel = new GlobePanel({
    mapManager,
    layerManager,
    labs,
    onMessage: showStatusMessage,
    exitView3D: async () => { if (view3dPanel?.controller) await view3dPanel.toggle(); }
  });
  globePanel.init();

  swipePanel = new SwipePanel({
    mapManager,
    layerManager,
    labs,
    onMessage: showStatusMessage,
    isView3DActive: () => !!view3dPanel?.controller,
    isGlobeActive: () => !!globePanel?.isActive()
  });
  swipePanel.init();

  // 6. 테마 토글 버튼 이벤트
  const themeToggle = document.getElementById('theme-toggle');
  themeToggle.addEventListener('click', () => {
    themeManager.toggle();
  });

  // 7. 상태표시줄 이벤트 바인딩
  initStatusBar();

  // 8. 툴바 이벤트 바인딩
  initToolbar();

  // 9. 윈도우 리사이즈 시 지도 크기 갱신
  window.addEventListener('resize', () => {
    mapManager.updateSize();
  });

  // 10. 메뉴바 초기화
  initMenubar();

  // 11. 위치 검색 초기화
  initLocationSearch();

  // 11.6 최근 파일 목록 초기화
  updateRecentFilesUI();

  // 12. 키보드 단축키 설정
  document.addEventListener('keydown', (e) => {
    // 입력 필드에서는 단축키 무시
    const activeEl = document.activeElement;
    const isInputField = activeEl && (
      activeEl.tagName === 'INPUT' ||
      activeEl.tagName === 'TEXTAREA' ||
      activeEl.isContentEditable
    );

    // Ctrl+Z: 실행 취소
    if (e.ctrlKey && e.key === 'z' && !e.shiftKey && !isInputField) {
      e.preventDefault();
      if (historyManager.undo()) {
        showStatusMessage('실행 취소되었습니다.');
      }
      return;
    }

    // Ctrl+Shift+Z 또는 Ctrl+Y: 다시 실행
    if ((e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'z') || (e.ctrlKey && e.key === 'y')) {
      e.preventDefault();
      if (!isInputField && historyManager.redo()) {
        showStatusMessage('다시 실행되었습니다.');
      }
      return;
    }

    // Ctrl+A: 전체 선택
    if (e.ctrlKey && e.key === 'a' && !isInputField) {
      e.preventDefault();
      selectTool.selectAll();
      return;
    }

    // Ctrl+C: 복사
    if (e.ctrlKey && e.key === 'c' && !isInputField) {
      const selectedFeatures = selectTool.getSelectedFeatures();
      if (selectedFeatures.length > 0) {
        e.preventDefault();
        copyFeaturesToClipboard(selectedFeatures);
      }
      return;
    }

    // Ctrl+V: 붙여넣기
    if (e.ctrlKey && e.key === 'v' && !isInputField) {
      e.preventDefault();
      pasteFromClipboard();
      return;
    }

    // Delete: 선택된 레이어 삭제 (다중 선택 지원)
    if (e.key === 'Delete' && !isInputField) {
      const selectedIds = layerManager.getSelectedLayerIds();
      if (selectedIds.length > 0) {
        const layers = selectedIds.map(id => layerManager.getLayer(id)).filter(Boolean);
        const layerNames = layers.map(l => l.name).join(', ');
        const message = selectedIds.length === 1
          ? `"${layerNames}" 레이어를 삭제하시겠습니까?`
          : `${selectedIds.length}개 레이어를 삭제하시겠습니까?\n(${layerNames})`;

        if (confirm(message)) {
          selectedIds.forEach(id => layerManager.removeLayer(id));
          showStatusMessage(`${selectedIds.length}개 레이어가 삭제되었습니다.`);
        }
      }
    }
  });

  // 레이어 추가 버튼 이벤트 (파일 추가)
  const btnAddLayer = document.getElementById('btn-add-layer');
  if (btnAddLayer) {
    btnAddLayer.addEventListener('click', () => {
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.geojson,.json,.zip,.shp,.dbf,.shx,.prj,.gpkg,.tif,.tiff,.img,image/tiff,application/octet-stream,*/*';
      fileInput.multiple = true;
      fileInput.style.display = 'none';
      document.body.appendChild(fileInput);

      fileInput.addEventListener('change', async (e) => {
        const files = Array.from(e.target.files);

        // shapefile 구성 파일은 basename별로 묶어 한 번에 로드 (속성까지 함께)
        const shpComponents = files.filter((f) => isShapefileComponent(f.name));
        const others = files.filter((f) => !isShapefileComponent(f.name));

        if (shpComponents.length > 0) {
          try {
            showStatusMessage('Shapefile 로딩 중...');
            const attemptedSets = countShapefileSets(shpComponents);
            const ids = await shapefileLoader.loadFromFiles(shpComponents);
            if (ids.length === 0) {
              // 좌표계 선택을 전부 취소한 경우: "로드 완료"로 보고하지 않고 최근 파일에도 남기지 않는다
              showStatusMessage('좌표계 선택을 취소했습니다: Shapefile');
            } else {
              // 일부만 만들어진 경우(다른 shapefile 세트의 좌표계 선택을 취소한 경우) 알려준다.
              // ShapefileLoader는 만들어진 layerId만 돌려주므로 정확한 스킵 개수는 알 수 없다 —
              // 시도한 세트 수(attemptedSets)보다 결과가 적을 때만 안내 문구를 덧붙인다.
              const partialNotice = ids.length < attemptedSets ? ' (일부 좌표계 선택 취소됨)' : '';
              showStatusMessage(`Shapefile ${ids.length}개 레이어 로드 완료${partialNotice}`);
              const shpFile = shpComponents.find((f) => /\.shp$/i.test(f.name));
              if (shpFile) addRecentFile(shpFile.name, 'shp');
            }
          } catch (error) {
            console.error('Shapefile 로드 실패:', error);
            showStatusMessage('Shapefile 로드 실패: ' + error.message);
          }
        }

        for (const file of others) {
          try {
            showStatusMessage('로딩 중: ' + file.name);
            const loadedId = await loadFileByExtension(file);
            if (loadedId === null) {
              showStatusMessage('좌표계 선택을 취소했습니다: ' + file.name);
              continue;
            }
            showStatusMessage(file.name + ' 로드 완료');
            // 최근 파일에 추가
            const ext = file.name.split('.').pop().toLowerCase();
            addRecentFile(file.name, ext);
          } catch (error) {
            console.error('파일 로드 실패:', error);
            showStatusMessage('파일 로드 실패: ' + error.message);
          }
        }
        fileInput.remove();
      });

      fileInput.click();
    });
  }

  console.log('eGIS 초기화 완료!');
}

/**
 * 상태표시줄 초기화
 */
function initStatusBar() {
  const coordsEl = document.querySelector('#status-coords .coord-value');
  const scaleInput = document.getElementById('scale-input');
  const crsEl = document.getElementById('status-crs');

  // 마우스 이동 시 좌표 업데이트 (선택된 좌표계로 변환)
  eventBus.on(Events.MAP_POINTER_MOVE, ({ lonLat }) => {
    const displayCRS = coordinateSystem.getDisplayCRS();

    if (displayCRS === 'EPSG:4326') {
      coordsEl.textContent = coordinateSystem.formatCoords(lonLat);
    } else {
      const transformed = coordinateSystem.transform(lonLat, 'EPSG:4326', displayCRS);
      coordsEl.textContent = coordinateSystem.formatCoords(transformed);
    }
  });

  // 지도 이동 시 축척 업데이트
  eventBus.on(Events.MAP_MOVEEND, () => {
    const scale = mapManager.getScale();
    if (document.activeElement !== scaleInput) {
      scaleInput.value = formatScale(scale);
    }
  });

  // CRS 변경 시 표시 업데이트
  eventBus.on(Events.CRS_CHANGED, ({ crs }) => {
    updateCRSDisplay(crs);
  });

  // 초기 축척 표시
  setTimeout(() => {
    const scale = mapManager.getScale();
    scaleInput.value = formatScale(scale);
  }, 100);

  // 축척 입력 처리
  if (scaleInput) {
    // Enter 키로 축척 적용
    scaleInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        applyScaleFromInput(scaleInput);
        scaleInput.blur();
      } else if (e.key === 'Escape') {
        // ESC로 취소하고 현재 축척으로 복원
        const scale = mapManager.getScale();
        scaleInput.value = formatScale(scale);
        scaleInput.blur();
      }
    });

    // 포커스 아웃 시 축척 적용
    scaleInput.addEventListener('blur', () => {
      applyScaleFromInput(scaleInput);
    });

    // 클릭 시 전체 선택
    scaleInput.addEventListener('focus', () => {
      scaleInput.select();
    });
  }

  // CRS 선택 드롭다운 초기화
  if (crsEl) {
    initCRSSelector(crsEl);
  }
}

/**
 * 입력된 축척 값을 지도에 적용
 */
function applyScaleFromInput(inputEl) {
  const inputValue = inputEl.value.replace(/,/g, '').trim();
  const targetScale = parseInt(inputValue, 10);

  if (isNaN(targetScale) || targetScale <= 0) {
    // 유효하지 않은 값이면 현재 축척으로 복원
    const scale = mapManager.getScale();
    inputEl.value = formatScale(scale);
    return;
  }

  // 축척을 줌 레벨로 변환하여 적용
  const targetZoom = mapManager.scaleToZoom(targetScale);
  if (targetZoom !== null) {
    mapManager.getView().animate({
      zoom: targetZoom,
      duration: 300
    });
  }
}

/**
 * CRS 선택 드롭다운 초기화
 */
function initCRSSelector(crsEl) {
  const dropdown = document.createElement('div');
  dropdown.className = 'crs-dropdown';
  
  const menuItems = coordinateSystem.getAvailableCRS().map(crs => 
    '<div class="crs-option" data-crs="' + crs.code + '">' +
      '<span class="crs-code">' + crs.code + '</span>' +
      '<span class="crs-name">' + crs.name + '</span>' +
    '</div>'
  ).join('');
  
  dropdown.innerHTML = '<div class="crs-dropdown-menu">' + menuItems + '</div>';
  crsEl.appendChild(dropdown);

  updateCRSDisplay(coordinateSystem.getDisplayCRS());

  crsEl.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdown.classList.toggle('open');
  });

  dropdown.querySelectorAll('.crs-option').forEach(option => {
    option.addEventListener('click', (e) => {
      e.stopPropagation();
      const crs = option.dataset.crs;
      coordinateSystem.setDisplayCRS(crs);
      dropdown.classList.remove('open');
    });
  });

  document.addEventListener('click', () => {
    dropdown.classList.remove('open');
  });
}

/**
 * CRS 표시 업데이트
 */
function updateCRSDisplay(crs) {
  const crsValue = document.querySelector('#status-crs .crs-value');
  if (crsValue) {
    crsValue.textContent = crs;
  }
}

/**
 * 축척 포맷팅 (천 단위 콤마)
 */
function formatScale(scale) {
  return Math.round(scale).toLocaleString('ko-KR');
}

/**
 * 툴바 초기화
 */
function initToolbar() {
  const toolbar = document.getElementById('toolbar');
  const view = mapManager.getView();

  toolbar.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-tool]');
    if (!btn) return;

    const tool = btn.dataset.tool;

    // 줌 관련 도구
    switch (tool) {
      case 'zoom-in':
        view.animate({ zoom: view.getZoom() + 1, duration: 250 });
        return;
      case 'zoom-out':
        view.animate({ zoom: view.getZoom() - 1, duration: 250 });
        return;
      case 'zoom-extent':
        view.animate({ center: view.getCenter(), zoom: 7, duration: 500 });
        return;
      case 'labs':
        labPanel.show();
        return;
      case 'geocoding':
        geocodingPanel.show();
        return;
      case 'swipe':
        swipePanel?.toggle();
        return;
      case 'globe':
        // 지구본과 스와이프는 배타 — 지구본을 켜고 끌 때 스와이프를 먼저 닫는다(닫혀 있으면 아무 일 없음)
        swipePanel?.close();
        globePanel?.toggle();
        return;
      case 'view3d':
        // 3D 와 스와이프는 배타 — 3D 를 켜고 끌 때 스와이프를 먼저 닫는다(닫혀 있으면 아무 일 없음)
        swipePanel?.close();
        view3dPanel?.toggle();
        return;
      case 'upload-image': {
        import('./ui/MapImageOverlay.js').then(({ triggerImageUpload }) => {
          triggerImageUpload(document.getElementById('map'));
        });
        return;
      }
      case 'clear-measures':
        // 도구를 켜는 것이 아니라 바로 실행되는 동작이다
        toolManager.clearMeasurements();
        showStatusMessage('측정 결과가 지워졌습니다.');
        return;
    }

    // 그리기/선택 도구
    toolManager.toggleTool(tool);
  });

  // 도구 묶음(선택·그리기·측정) 펼치기 — 태블릿 폭에서 툴바가 넘치지 않게 접어 둔다
  const toolbarGroups = initToolbarGroups({
    toolbar,
    getCurrentTool: () => toolManager.getCurrentTool(),
    deactivateCurrentTool: () => {
      const current = toolManager.getCurrentTool();
      if (current) toolManager.toggleTool(current);
    },
  });
  eventBus.on(Events.TOOL_CHANGED, ({ tool }) => toolbarGroups.sync(tool));

  // 태블릿에서 카피라이트는 ⓒ 버튼 → 말풍선
  initCopyrightToggle(document);

  // 전체화면 버튼 (iPad 등 지원 기기에서만 보인다)
  initFullscreenToggle(document);

  // 홈 화면에 추가 (태블릿·휴대폰에서만)
  initInstallPrompt(document);

  // 선택 액션 버튼 (선택 취소 / 선택 피처 삭제) — 선택 도구의 선택 집합에 따라 표시
  const btnClearSel = document.getElementById('btn-clear-selection');
  const btnDeleteSel = document.getElementById('btn-delete-selection');

  if (btnClearSel) {
    btnClearSel.addEventListener('click', () => toolManager.clearSelection());
  }
  if (btnDeleteSel) {
    btnDeleteSel.addEventListener('click', () => {
      if (selectTool.getSelectedFeatures().length === 0) {
        showStatusMessage('삭제할 피처를 먼저 선택하세요.');
        return;
      }
      // deleteSelectedFeatures()가 확인창을 띄운다. 취소하면 false 반환.
      const deleted = toolManager.deleteSelectedFeatures();
      if (deleted) showStatusMessage('선택된 피처가 삭제되었습니다.');
    });
  }

  // 속성정보 보기 — 선택 도구가 켜져 있는 동안에만 나타나는 토글
  const btnFeatureInfo = document.getElementById('btn-feature-info');
  if (btnFeatureInfo) {
    const syncFeatureInfoBtn = () => {
      btnFeatureInfo.classList.toggle('active', featureInfoCard.isEnabled());
    };
    featureInfoCard.onChange = syncFeatureInfoBtn;

    btnFeatureInfo.addEventListener('click', () => {
      featureInfoCard.setEnabled(!featureInfoCard.isEnabled());
    });

    const hideFeatureInfo = () => {
      btnFeatureInfo.style.display = 'none';
      featureInfoCard.setEnabled(false);
    };

    eventBus.on(Events.TOOL_ACTIVATED, ({ tool }) => {
      if (tool !== 'select') return;
      btnFeatureInfo.style.display = '';
      syncFeatureInfoBtn();
    });

    eventBus.on(Events.TOOL_DEACTIVATED, ({ tool }) => {
      if (tool !== 'select') return;
      hideFeatureInfo();
    });

    eventBus.on(Events.PROJECT_NEW, () => hideFeatureInfo());
  }

  // 피처 합치기 (선택한 피처들을 하나로) — 모드가 아닌 즉시 액션
  const btnMerge = document.getElementById('btn-merge-features');
  if (btnMerge) {
    btnMerge.addEventListener('click', () => featureEditTool.mergeSelected());
  }

  // 선택 개수가 바뀌면 버튼 표시/숨김 + 속성 카드 갱신
  eventBus.on(Events.SELECTION_CHANGED, ({ count }) => {
    const display = count > 0 ? '' : 'none';
    if (btnClearSel) btnClearSel.style.display = display;
    if (btnDeleteSel) btnDeleteSel.style.display = display;
    featureInfoCard.refresh();
  });
}


/**
 * 메뉴바 초기화
 */
function initMenubar() {
  // 드롭다운 토글
  // 휴대폰에서는 MobileShell 이 .menu-center 를 서랍(#mobile-drawer)으로 옮기므로 document 에 건다
  document.addEventListener('click', (e) => {
    if (!e.target.closest('#menubar, #mobile-drawer')) return;
    // .menu-button: 일반 메뉴, .btn-community: 링크 모양 버튼(Geocoding·About e-GIS)
    const menuButton = e.target.closest('.menu-button, .btn-community');
    if (menuButton) {
      const menuItem = menuButton.closest('.menu-item');
      const isDropdown = menuItem?.classList.contains('dropdown');

      // 드롭다운이 아닌 버튼(데이터 불러오기·Geocoding)은 바로 실행
      const action = menuButton.dataset.action;
      if (action && !isDropdown) {
        handleMenuAction(action);
        return;
      }
      if (!isDropdown) return;

      // 다른 드롭다운 닫기
      document.querySelectorAll('.menu-item.dropdown.open').forEach(item => {
        if (item !== menuItem) item.classList.remove('open');
      });

      // 현재 메뉴 토글
      menuItem.classList.toggle('open');
      return;
    }

    // 드롭다운 메뉴 아이템 클릭
    const dropdownItem = e.target.closest('.dropdown-item');
    if (dropdownItem) {
      // 링크 항목(GUIDE·커뮤니티·e-GIStory)은 액션 없이 새 탭으로 열린다
      const action = dropdownItem.dataset.action;
      if (action) handleMenuAction(action);

      // 드롭다운 닫기
      document.querySelectorAll('.menu-item.dropdown.open').forEach(item => {
        item.classList.remove('open');
      });
    }
  });

  // 외부 클릭 시 드롭다운 닫기
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.menu-item')) {
      document.querySelectorAll('.menu-item.dropdown.open').forEach(item => {
        item.classList.remove('open');
      });
    }
  });
}

/**
 * 메뉴 액션 처리
 */
function handleMenuAction(action) {
  switch (action) {
    case 'project-new':
      if (projectManager.hasDirty()) {
        if (confirm('저장하지 않은 변경사항이 있습니다. 새 프로젝트를 시작하시겠습니까?')) {
          projectManager.newProject();
          showStatusMessage('새 프로젝트가 생성되었습니다.');
        }
      } else {
        projectManager.newProject();
        showStatusMessage('새 프로젝트가 생성되었습니다.');
      }
      break;
      
    case 'project-open':
      projectManager.loadFromFile()
        .then(() => {
          showStatusMessage('프로젝트를 불러왔습니다: ' + projectManager.getProjectName());
        })
        .catch((error) => {
          showStatusMessage('프로젝트 불러오기 실패: ' + error.message);
        });
      break;
      
    case 'project-save': {
      // Electron webview는 prompt() 미지원 → askText(데스크톱=모달, 브라우저=prompt)
      askText('프로젝트 이름을 입력하세요:', projectManager.getProjectName()).then((newName) => {
        if (!newName || !newName.trim()) return;
        projectManager.setProjectName(newName.trim());
        projectManager.saveToFile().then((ok) => {
          if (ok) showStatusMessage('프로젝트가 저장되었습니다: ' + newName.trim());
        });
      });
      break;
    }
    case 'project-export':
      exportPanel.show();
      break;

    // ===== 편집 메뉴 =====
    case 'edit-merge':
      featureEditTool.mergeSelected();
      break;
    case 'edit-split':
      toolManager.toggleTool('edit-split');
      break;
    case 'edit-delete': {
      // 현재 선택된 피처 삭제. activateTool('select')를 다시 호출하면
      // Select 인터랙션이 재생성되며 선택이 초기화되므로 호출하지 않는다.
      if (selectTool.getSelectedFeatures().length === 0) {
        showStatusMessage('삭제할 피처를 먼저 선택하세요.');
        break;
      }
      // deleteSelectedFeatures()가 확인창을 띄운다. 취소하면 false 반환(메시지 없음).
      const deleted = toolManager.deleteSelectedFeatures();
      if (deleted) {
        showStatusMessage('선택된 피처가 삭제되었습니다.');
      }
      break;
    }
    case 'edit-select-all': {
      const selectedLayerId = layerManager.getSelectedLayerId();
      if (!selectedLayerId) {
        showStatusMessage('먼저 레이어 패널에서 레이어를 선택하세요.');
        break;
      }
      toolManager.activateTool('select');
      toolManager.selectAllFeatures();
      const layer = layerManager.getLayer(selectedLayerId);
      const count = layer ? layer.source.getFeatures().length : 0;
      showStatusMessage(count + '개 피처가 선택되었습니다.');
      break;
    }
    case 'edit-deselect':
      toolManager.clearSelection();
      showStatusMessage('선택이 해제되었습니다.');
      break;
    // ===== 보기 메뉴 =====
    case 'view-zoom-in':
      mapManager.getView().animate({ zoom: mapManager.getView().getZoom() + 1, duration: 250 });
      break;
    case 'view-zoom-out':
      mapManager.getView().animate({ zoom: mapManager.getView().getZoom() - 1, duration: 250 });
      break;
    case 'view-full-extent':
      mapManager.getView().animate({ center: mapManager.getView().getCenter(), zoom: 7, duration: 500 });
      break;
    case 'view-zoom-layer': {
      const selectedId = layerManager.getSelectedLayerId();
      if (selectedId) {
        layerManager.zoomToLayer(selectedId);
        showStatusMessage('선택된 레이어로 이동했습니다.');
      } else {
        showStatusMessage('먼저 레이어를 선택해주세요.');
      }
      break;
    }
    case 'view-toggle-panel': {
      layout.toggleSidebar();
      mobileShell?.syncLayersBtn();
      mapManager.updateSize();
      break;
    }
    case 'view-bookmarks':
      bookmarkPanel.show();
      break;

    // ===== 레이어 메뉴 =====
    case 'layer-add': {
      askText('새 레이어 이름:', '새 레이어').then((layerName) => {
        if (layerName && layerName.trim()) {
          layerManager.addLayer({ name: layerName.trim() });
          showStatusMessage('레이어가 추가되었습니다: ' + layerName.trim());
        }
      });
      break;
    }
    case 'layer-from-coords':
      coordinateImportPanel.show();
      break;
    case 'layer-remove': {
      const selectedLayerId = layerManager.getSelectedLayerId();
      if (selectedLayerId) {
        const layer = layerManager.getLayer(selectedLayerId);
        if (confirm('"' + layer.name + '" 레이어를 삭제하시겠습니까?')) {
          layerManager.removeLayer(selectedLayerId);
          showStatusMessage('레이어가 삭제되었습니다.');
        }
      } else {
        showStatusMessage('먼저 레이어를 선택해주세요.');
      }
      break;
    }
    case 'layer-rename': {
      const selLayerId = layerManager.getSelectedLayerId();
      if (selLayerId) {
        const layer = layerManager.getLayer(selLayerId);
        askText('새 이름:', layer.name).then((newName) => {
          if (newName && newName.trim()) {
            layerManager.renameLayer(selLayerId, newName.trim());
            showStatusMessage('레이어 이름이 변경되었습니다.');
          }
        });
      } else {
        showStatusMessage('먼저 레이어를 선택해주세요.');
      }
      break;
    }
    case 'layer-attribute-table': {
      const attrLayerId = layerManager.getSelectedLayerId();
      if (attrLayerId) {
        attributeTable.open(attrLayerId);
      } else {
        showStatusMessage('먼저 레이어를 선택해주세요.');
      }
      break;
    }
    case 'layer-label': {
      labelPanel.open();
      break;
    }
    case 'layer-table-join':
      tableJoinPanel.show();
      break;
    case 'layer-field-calculator':
      fieldCalculatorPanel.show();
      break;
    case 'layer-merge':
      layerMergePanel.show();
      break;
    case 'layer-split':
      layerSplitPanel.show();
      break;
    case 'layer-export':
      layerExportPanel.show();
      break;
    case 'layer-clear-all':
      if (confirm('모든 레이어를 삭제하시겠습니까?')) {
        layerManager.clear();
        showStatusMessage('모든 레이어가 삭제되었습니다.');
      }
      break;

    // ===== 분석 메뉴 =====
    case 'analysis-measure-distance':
      toolManager.activateTool('measure-distance');
      showStatusMessage('거리 측정: 지도를 클릭하여 측정하세요. 더블클릭으로 종료.');
      break;
    case 'analysis-measure-area':
      toolManager.activateTool('measure-area');
      showStatusMessage('면적 측정: 지도를 클릭하여 측정하세요. 더블클릭으로 종료.');
      break;
    case 'analysis-clear-measures':
      toolManager.clearMeasurements();
      showStatusMessage('측정 결과가 지워졌습니다.');
      break;
    case 'analysis-choropleth':
      choroplethPanel.show();
      break;
    case 'analysis-time-series':
      if (!labs.isOn('time-series')) break;   // 메뉴가 숨겨져 있어도 단축 경로로 올 수 있다
      timeSeriesPanel.show();
      break;
    case 'analysis-grid':
      gridPanel.show();
      break;
    case 'analysis-buffer':
      bufferPanel.show();
      break;
    case 'analysis-voronoi':
      voronoiPanel.show();
      break;
    case 'analysis-spatial-ops':
      spatialOperationsPanel.show();
      break;
    case 'analysis-heatmap':
      heatmapPanel.show();
      break;
    case 'analysis-chart-map':
      chartMapPanel.show();
      break;
    case 'analysis-cartogram':
      cartogramPanel.show();
      break;
    case 'analysis-flow':
      flowPanel.show();
      break;
    case 'analysis-isochrone':
      isochronePanel.show();
      break;
    case 'analysis-routing':
      routingPanel.show();
      break;

    // ===== 래스터 분석 메뉴 =====
    case 'analysis-terrain':
      rasterAnalysisPanel.showTerrain();
      break;
    case 'analysis-raster-filter':
      rasterAnalysisPanel.showRasterFilter();
      break;
    case 'analysis-slope':
      rasterAnalysisPanel.showSlope();
      break;
    case 'analysis-aspect':
      rasterAnalysisPanel.showAspect();
      break;
    case 'analysis-contour':
      rasterAnalysisPanel.showContour();
      break;
    case 'georeference':
      georeferencePanel.show();
      break;

    // ===== 내장 데이터 =====
    case 'builtin-data':
      builtinDataDialog.show();
      break;

    // ===== Geocoding 안내창 =====
    case 'geocoding':
      geocodingPanel.show();
      break;

    default:
      console.log('메뉴 액션:', action);
  }
}

/**
 * shapefile 구성 파일 목록에서 실제로 시도되는 shapefile 세트 개수를 센다.
 * ShapefileLoader.loadFromFiles는 .shp가 없는 묶음은 건너뛰므로, 여기서도
 * .shp 파일 기준으로 basename을 세어 "몇 개를 시도했는지"에 맞춘다.
 * (반환값은 만들어진 layerId 배열뿐이라 취소로 일부가 빠졌는지 직접 알 수 없어
 *  main.js에서 이렇게 별도로 센다 — ShapefileLoader의 반환 형태는 바꾸지 않는다)
 */
function countShapefileSets(files) {
  const basenames = new Set();
  for (const f of files) {
    if (/\.shp$/i.test(f.name)) {
      basenames.add(f.name.replace(/\.shp$/i, '').toLowerCase());
    }
  }
  return basenames.size;
}

/**
 * 레이어 추가 메뉴 표시
 */
async function loadFileByExtension(file) {
  const ext = file.name.split('.').pop().toLowerCase();

  switch (ext) {
    case 'geojson':
    case 'json':
      return await geojsonLoader.loadFromFile(file);
    case 'zip':
    case 'shp':
    case 'dbf':
    case 'shx':
    case 'prj':
      return await shapefileLoader.loadFromFile(file);
    case 'gpkg':
      return await geopackageLoader.loadFromFile(file);
    case 'tif':
    case 'tiff':
    case 'img':
      return await demLoader.loadFromFile(file);
    default:
      throw new Error('지원하지 않는 파일 형식입니다. (GeoJSON, ZIP, GPKG, TIF, IMG 지원)');
  }
}

/**
 * 상태 메시지 표시
 */
function showStatusMessage(message) {
  const statusMessage = document.getElementById('status-message');
  if (statusMessage) {
    statusMessage.textContent = message;

    // 3초 후 기본 메시지로 복원
    setTimeout(() => {
      statusMessage.textContent = '준비';
    }, 3000);
  }
}

/**
 * 준비중 모달 표시
 */
function showComingSoonModal(featureName) {
  // 기존 모달이 있으면 제거
  const existingModal = document.getElementById('coming-soon-modal');
  if (existingModal) {
    existingModal.remove();
  }

  const modalHtml = `
    <div class="modal-overlay active" id="coming-soon-modal">
      <div class="modal" style="width: 300px;">
        <div class="modal-header">
          <h3>${featureName}</h3>
          <button class="modal-close" id="coming-soon-close">&times;</button>
        </div>
        <div class="modal-body" style="padding: var(--spacing-lg); text-align: center;">
          <p style="margin: 0; color: var(--text-secondary);">준비중입니다.</p>
        </div>
        <div class="modal-footer" style="padding: var(--spacing-md) var(--spacing-lg); border-top: 1px solid var(--border-color); text-align: right;">
          <button class="btn btn-primary btn-sm" id="coming-soon-ok">확인</button>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', modalHtml);

  const modal = document.getElementById('coming-soon-modal');
  const closeBtn = document.getElementById('coming-soon-close');
  const okBtn = document.getElementById('coming-soon-ok');

  const closeModal = () => {
    modal.classList.remove('active');
    setTimeout(() => modal.remove(), 200);
  };

  closeBtn.addEventListener('click', closeModal);
  okBtn.addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });
}

/**
 * 위치 검색 초기화
 */
function initLocationSearch() {
  const searchInput = document.getElementById('location-search-input');
  const searchResults = document.getElementById('search-results');
  const searchClear = document.getElementById('search-clear');

  if (!searchInput || !searchResults) return;

  let debounceTimer = null;
  let selectedIndex = -1;
  let results = [];

  // 입력 이벤트
  searchInput.addEventListener('input', () => {
    const query = searchInput.value.trim();

    // 지우기 버튼 표시/숨김
    searchClear.style.display = query ? 'flex' : 'none';

    // 디바운스
    clearTimeout(debounceTimer);

    if (query.length < 2) {
      hideResults();
      return;
    }

    // 로딩 표시
    searchResults.style.display = 'block';
    searchResults.innerHTML = '<div class="search-loading"></div>';

    debounceTimer = setTimeout(async () => {
      results = await geocodingService.search(query);
      selectedIndex = -1;
      renderResults(results);
    }, 300);
  });

  // 키보드 네비게이션
  searchInput.addEventListener('keydown', (e) => {
    if (!results.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        selectedIndex = Math.min(selectedIndex + 1, results.length - 1);
        updateSelection();
        break;
      case 'ArrowUp':
        e.preventDefault();
        selectedIndex = Math.max(selectedIndex - 1, 0);
        updateSelection();
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          selectResult(results[selectedIndex]);
        } else if (results.length > 0) {
          selectResult(results[0]);
        }
        break;
      case 'Escape':
        hideResults();
        searchInput.blur();
        break;
    }
  });

  // 포커스 아웃 시 결과 숨김
  searchInput.addEventListener('blur', () => {
    setTimeout(() => hideResults(), 200);
  });

  // 지우기 버튼
  searchClear.addEventListener('click', () => {
    searchInput.value = '';
    searchClear.style.display = 'none';
    hideResults();
    searchInput.focus();
  });

  // 결과 렌더링
  function renderResults(items) {
    if (!items.length) {
      searchResults.innerHTML = '<div class="search-empty"></div>';
      return;
    }

    searchResults.innerHTML = items.map((item, index) => `
      <div class="search-result-item" data-index="${index}">
        <div class="search-result-name">
          ${item.category ? `<span class="search-result-type">${getTypeLabel(item.category)}</span>` : ''}
          ${item.shortName}
        </div>
        <div class="search-result-address">${item.displayName}</div>
      </div>
    `).join('');

    // 클릭 이벤트
    searchResults.querySelectorAll('.search-result-item').forEach(el => {
      el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        const index = parseInt(el.dataset.index);
        selectResult(results[index]);
      });
    });
  }

  // 선택 상태 업데이트
  function updateSelection() {
    searchResults.querySelectorAll('.search-result-item').forEach((el, idx) => {
      el.classList.toggle('selected', idx === selectedIndex);
      if (idx === selectedIndex) {
        el.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  // 결과 선택 - 지도 이동
  function selectResult(item) {
    hideResults();
    searchInput.value = item.shortName;

    // 지도 이동
    if (item.boundingBox) {
      // boundingBox: [south, north, west, east]
      const extent = [
        parseFloat(item.boundingBox[2]), // west (minX)
        parseFloat(item.boundingBox[0]), // south (minY)
        parseFloat(item.boundingBox[3]), // east (maxX)
        parseFloat(item.boundingBox[1])  // north (maxY)
      ];

      // EPSG:4326 → EPSG:3857 변환
      const transformedExtent = transformExtent(extent, 'EPSG:4326', 'EPSG:3857');
      mapManager.fitExtent(transformedExtent, { padding: [50, 50, 50, 50], maxZoom: 18 });
    } else {
      // 중심점으로 이동
      const center = fromLonLat([item.lon, item.lat]);
      mapManager.getView().animate({
        center: center,
        zoom: 15,
        duration: 500
      });
    }

    showStatusMessage(`${item.shortName}(으)로 이동했습니다.`);
  }

  // 결과 숨김
  function hideResults() {
    searchResults.style.display = 'none';
    results = [];
    selectedIndex = -1;
  }

  // 카테고리 한글화
  function getTypeLabel(category) {
    const labels = {
      'amenity': '시설',
      'building': '건물',
      'place': '장소',
      'highway': '도로',
      'natural': '자연',
      'tourism': '관광',
      'shop': '상점',
      'office': '사무실',
      'leisure': '레저',
      'boundary': '경계',
      'landuse': '토지',
      'waterway': '수로'
    };
    return labels[category] || category;
  }
}

/**
 * 헤더 인증 버튼 이벤트 초기화
 */
function initHeaderAuth() {
  const headerAuth = document.getElementById('header-auth');
  if (!headerAuth) return;

  headerAuth.addEventListener('click', (e) => {
    const btn = e.target.closest('button');
    if (!btn) return;

    if (btn.id === 'header-login-btn') {
      cloudPanel.show();
    } else if (btn.id === 'header-mypage-btn') {
      myPagePanel.show();
    } else if (btn.id === 'header-logout-btn') {
      supabaseManager.signOut().then(() => {
        updateHeaderAuth();
        showStatusMessage('로그아웃되었습니다.');
      });
    }
  });

  // 인증 상태 변경 이벤트 구독
  // 로그인 직후엔 아직 프로필이 없으므로 이메일로 먼저 그리고, 닉네임을 받으면 다시 그린다.
  eventBus.on('auth:login', () => {
    updateHeaderAuth();
    supabaseManager.loadProfileCache().then(() => updateHeaderAuth());
  });
  eventBus.on('auth:logout', () => updateHeaderAuth());
  eventBus.on('auth:profile-updated', () => updateHeaderAuth());
}

/**
 * 헤더 인증 상태 업데이트
 */
function updateHeaderAuth() {
  const headerAuth = document.getElementById('header-auth');
  if (!headerAuth) return;

  if (supabaseManager.isLoggedIn()) {
    const user = supabaseManager.getUser();
    const isAdmin = supabaseManager.isAdmin();
    const nickname = supabaseManager.getNickname();

    // 닉네임이 있으면 "닉네임(이메일)" — 태블릿·모바일에서는 CSS 가 괄호 부분을 숨겨 닉네임만 남는다.
    // 닉네임을 설정하지 않았으면 이메일만 표시한다.
    const userLabel = nickname
      ? `<span class="header-user-name">${escapeHtml(nickname)}</span><span class="header-user-mail">(${escapeHtml(user.email)})</span>`
      : `<span class="header-user-name">${escapeHtml(user.email)}</span>`;

    headerAuth.innerHTML = `
      <span class="header-user-email">${userLabel}${isAdmin ? ' <span class="admin-badge">관리자</span>' : ''}</span>
      <button class="btn btn-sm btn-primary" id="header-mypage-btn"><span class="auth-label-ko">마이페이지</span><span class="auth-label-en">Mypage</span></button>
      <button class="btn btn-sm btn-secondary" id="header-logout-btn"><span class="auth-label-ko">로그아웃</span><span class="auth-label-en">Logout</span></button>
    `;
  } else {
    headerAuth.innerHTML = `
      <button class="btn btn-sm btn-primary" id="header-login-btn"><span class="auth-label-ko">로그인</span><span class="auth-label-en">Login</span></button>
    `;
  }
}

// 클립보드 (피처 복사/붙여넣기용)
let featureClipboard = [];

/** 3D 보기 패널 — 진단 훅에서도 쓴다 */
let view3dPanel = null;

/** 스와이프 비교 패널(실험실 swipe) — 진단 훅에서도 쓴다 */
let swipePanel = null;

/** 지구본·투영법 보기 패널(실험실 globe) — 진단 훅에서도 쓴다 */
let globePanel = null;

/** 열려 있는 애니메이션 저장 대화상자 — 새 프로젝트·실험 끄기 때 캡처를 중단한다 */
let animationDialog = null;

// 최근 파일 관리 (최대 5개)
const RECENT_FILES_KEY = 'egis_recent_files';
const MAX_RECENT_FILES = 5;

/**
 * 최근 파일 목록 가져오기
 */
function getRecentFiles() {
  try {
    const stored = localStorage.getItem(RECENT_FILES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * 최근 파일 추가
 */
function addRecentFile(fileName, fileType) {
  const recentFiles = getRecentFiles();

  // 동일한 파일이 있으면 제거
  const filtered = recentFiles.filter(f => f.name !== fileName);

  // 앞에 추가
  filtered.unshift({
    name: fileName,
    type: fileType,
    timestamp: Date.now()
  });

  // 최대 개수 제한
  const limited = filtered.slice(0, MAX_RECENT_FILES);

  localStorage.setItem(RECENT_FILES_KEY, JSON.stringify(limited));
  updateRecentFilesUI();
}

/**
 * 최근 파일 UI 업데이트
 */
function updateRecentFilesUI() {
  const recentFilesList = document.getElementById('recent-files-list');
  if (!recentFilesList) return;

  const recentFiles = getRecentFiles();

  if (recentFiles.length === 0) {
    recentFilesList.innerHTML = '<div class="dropdown-item disabled">최근 파일 없음</div>';
    return;
  }

  recentFilesList.innerHTML = recentFiles.map((file, index) => `
    <div class="dropdown-item" data-action="recent-file" data-index="${index}" title="${escapeHtml(file.name)}">
      ${escapeHtml(file.name)}
    </div>
  `).join('');
}

/**
 * 피처를 클립보드에 복사
 */
function copyFeaturesToClipboard(features) {
  if (!features || features.length === 0) {
    showStatusMessage('복사할 피처가 없습니다.');
    return;
  }

  // GeoJSON 형식으로 피처 복사
  const geojsonFormat = new GeoJSON();
  featureClipboard = features.map(feature => {
    return geojsonFormat.writeFeatureObject(feature, {
      featureProjection: 'EPSG:3857'
    });
  });

  showStatusMessage(`${features.length}개 피처가 복사되었습니다.`);
}

/**
 * 클립보드에서 피처 붙여넣기
 */
function pasteFromClipboard() {
  if (featureClipboard.length === 0) {
    showStatusMessage('붙여넣을 피처가 없습니다.');
    return;
  }

  const selectedLayerId = layerManager.getSelectedLayerId();
  if (!selectedLayerId) {
    showStatusMessage('붙여넣을 레이어를 먼저 선택하세요.');
    return;
  }

  const layerInfo = layerManager.getLayer(selectedLayerId);
  if (!layerInfo) return;

  const geojsonFormat = new GeoJSON();

  featureClipboard.forEach(featureObj => {
    // 약간 오프셋을 주어 구분 가능하게 함
    const clonedObj = JSON.parse(JSON.stringify(featureObj));

    const feature = geojsonFormat.readFeature(clonedObj, {
      featureProjection: 'EPSG:3857'
    });

    // 새 ID 부여
    feature.setId(Date.now() + '_' + Math.random().toString(36).substr(2, 9));

    layerInfo.source.addFeature(feature);
  });

  layerInfo.featureCount = layerInfo.source.getFeatures().length;
  eventBus.emit(Events.LAYER_ADDED, {});

  showStatusMessage(`${featureClipboard.length}개 피처가 붙여넣기 되었습니다.`);
}

// DOM 로드 완료 후 앱 초기화
document.addEventListener('DOMContentLoaded', initApp);

// 진단용 훅 — 헤드리스 재현 테스트(버그 리포트 검증)에서 내부 상태 접근용.
// 클라이언트 앱이라 보안 경계 아님(모든 코드·키가 이미 번들에 공개).
window.__egisDebug = { projectManager, layerManager, exportPanel, isochroneTool, roadNetwork, measureTool, selectTool, historyManager, mapManager, labs, choroplethTool, builtinDataManager, classFillPopover, geojsonLoader, timeSeriesTool, timeSeriesPanel, get view3dPanel() { return view3dPanel; }, get swipePanel() { return swipePanel; }, get globePanel() { return globePanel; }, get mobileShell() { return mobileShell; } };
